import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import {
  Alert,
  CustomInput,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import http from "../../../utils/http-common";
import { isAbortError } from "../../../utils/asyncRequestGuards";

const CURRENCIES = ["eur", "usd", "gbp", "chf", "cad", "aud"];

function money(amount, currency) {
  const c = (currency || "EUR").toUpperCase();
  if (amount == null) return "—";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: c }).format(Number(amount));
  } catch {
    return `${Number(amount)} ${c}`;
  }
}

function stripTcLabel(label) {
  return String(label || "")
    .replace(/\s*\(TC\)\s*$/i, "")
    .trim();
}

async function fetchTreeNodes(parentId, depth = 0) {
  const res = await http.get("/api/v1/category/public", {
    params: { parentId },
    suppressInlineErrorSwal: true,
  });
  const list = Array.isArray(res.data?.data) ? res.data.data : [];
  const sorted = [...list].sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
  const nodes = [];
  for (const row of sorted) {
    if (!row?._id) continue;
    const children = await fetchTreeNodes(row._id, depth + 1);
    nodes.push({
      id: String(row._id),
      label: stripTcLabel(row.label),
      depth,
      countryCode: row.countryCode ? String(row.countryCode).toUpperCase() : "",
      defaultCurrency: row.defaultCurrency ? String(row.defaultCurrency).toLowerCase() : "",
      children,
    });
  }
  return nodes;
}

/** TC specialty picker: canonical GET /public/tc-specialties (same filters as patient tunnel). */
async function fetchTcSpecialtyNodes() {
  const res = await http.get("/api/v1/public/tc-specialties", { suppressInlineErrorSwal: true });
  const list = Array.isArray(res.data?.data) ? res.data.data : [];
  return list
    .filter((row) => row?._id)
    .map((row) => ({
      id: String(row._id),
      label: stripTcLabel(row.label),
      depth: 0,
      countryCode: "",
      defaultCurrency: "",
      children: [],
    }));
}

function flattenNodes(nodes, out = []) {
  nodes.forEach((n) => {
    out.push(n);
    if (n.children?.length) flattenNodes(n.children, out);
  });
  return out;
}

function coverageSpecs(row) {
  if (row.allSpecialties) return "All";
  const labels = (row.specialties || [])
    .map((s) => (s && typeof s === "object" ? stripTcLabel(s.label) : null))
    .filter(Boolean);
  if (labels.length) return labels.join(", ");
  if (row.specialty?.label) return stripTcLabel(row.specialty.label);
  return "—";
}

function coverageCountries(row) {
  if (row.allCountries) return "All";
  if (Array.isArray(row.countries) && row.countries.length) return row.countries.join(", ");
  if (row.countryCode) return String(row.countryCode).toUpperCase();
  return "—";
}

function displayPrice(row) {
  const amount = row.defaultPrice?.amount ?? row.price;
  const currency = row.defaultPrice?.currency || row.currency;
  return money(amount, currency);
}

const emptyForm = () => ({
  name: "",
  description: "",
  allSpecialties: false,
  specialtyIds: [],
  allCountries: false,
  countryCodes: [],
  defaultAmount: "",
  defaultCompareAtAmount: "",
  defaultCurrency: "eur",
  prices: [],
  features: [{ label: "", value: "" }],
  badgeLabel: "",
  highlighted: false,
  includesVisio: false,
  slaDays: 7,
  active: true,
});

function formFromOffer(row) {
  if (!row) return emptyForm();
  const specialtyIds = row.allSpecialties
    ? []
    : (row.specialties || []).map((s) => (s && typeof s === "object" ? String(s._id) : String(s)));
  if (!specialtyIds.length && row.specialty) {
    const leg = typeof row.specialty === "object" ? row.specialty._id : row.specialty;
    if (leg) specialtyIds.push(String(leg));
  }
  let countryCodes = row.allCountries
    ? []
    : (row.countries || []).map((c) => String(c).toUpperCase());
  if (!countryCodes.length && row.countryCode) countryCodes = [String(row.countryCode).toUpperCase()];

  const features =
    Array.isArray(row.features) && row.features.length
      ? row.features.map((f) => ({ label: f.label || "", value: f.value || "" }))
      : [{ label: "", value: "" }];

  return {
    name: row.name || "",
    description: row.description || "",
    allSpecialties: Boolean(row.allSpecialties),
    specialtyIds: [...new Set(specialtyIds)],
    allCountries: Boolean(row.allCountries),
    countryCodes: [...new Set(countryCodes)],
    defaultAmount: String(row.defaultPrice?.amount ?? row.price ?? ""),
    defaultCompareAtAmount:
      row.defaultCompareAt?.amount != null ? String(row.defaultCompareAt.amount) : "",
    defaultCurrency: String(row.defaultPrice?.currency || row.currency || "eur").toLowerCase(),
    prices: (row.prices || []).map((p) => ({
      countryCode: String(p.countryCode || "").toUpperCase(),
      amount: String(p.amount ?? ""),
      compareAtAmount: p.compareAtAmount != null ? String(p.compareAtAmount) : "",
      currency: String(p.currency || "eur").toLowerCase(),
    })),
    features,
    badgeLabel: row.badgeLabel || "",
    highlighted: Boolean(row.highlighted),
    includesVisio: Boolean(row.includesVisio),
    slaDays: row.slaDays != null ? Number(row.slaDays) : 7,
    active: row.active !== false,
  };
}

function TreePicker({ nodes, loading, selectedIds, allFlag, allLabel, onToggleAll, onToggleId, idKey = "id" }) {
  return (
    <div>
      <FormGroup check className="mb-2">
        <CustomInput
          type="checkbox"
          id={`all-${allLabel}`}
          checked={allFlag}
          onChange={(e) => onToggleAll(e.target.checked)}
          label={allLabel}
        />
      </FormGroup>
      {!allFlag ? (
        loading ? (
          <p className="small text-muted mb-0">Loading…</p>
        ) : nodes.length === 0 ? (
          <p className="small text-muted mb-0">Tree unavailable. Check Platform catalog roots.</p>
        ) : (
          <div className="border rounded p-2" style={{ maxHeight: 260, overflowY: "auto" }}>
            {nodes.map((node) => {
              const key = idKey === "countryCode" ? node.countryCode : node.id;
              if (idKey === "countryCode" && !node.countryCode) return null;
              return (
                <FormGroup check key={key} className="mb-1">
                  <CustomInput
                    type="checkbox"
                    id={`pick-${allLabel}-${key}`}
                    checked={selectedIds.includes(key)}
                    onChange={() => onToggleId(key, node)}
                    label={<span style={{ paddingLeft: node.depth * 12 }}>{node.label || key}</span>}
                  />
                </FormGroup>
              );
            })}
          </div>
        )
      ) : null}
    </div>
  );
}

const SoOffers = () => {
  const history = useHistory();
  const location = useLocation();
  const duplicateId = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return q.get("duplicate") || "";
  }, [location.search]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [specFlat, setSpecFlat] = useState([]);
  const [countryFlat, setCountryFlat] = useState([]);
  const [treesLoading, setTreesLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(null);
  const [picker, setPicker] = useState(null);

  const countryOptions = useMemo(
    () => countryFlat.filter((n) => n.countryCode),
    [countryFlat]
  );

  const countryLabelByCode = useMemo(() => {
    const map = {};
    countryOptions.forEach((n) => {
      map[n.countryCode] = n.label || n.countryCode;
    });
    return map;
  }, [countryOptions]);

  const currencyForCountry = useCallback(
    (code) => {
      const node = countryOptions.find((n) => n.countryCode === code);
      const cur = node?.defaultCurrency;
      if (cur && CURRENCIES.includes(cur)) return cur;
      if (cur && cur.length === 3) return cur;
      return "eur";
    },
    [countryOptions]
  );

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError("");
    try {
      const res = await http.get("/api/v1/admin/second-opinion/offers", {
        signal,
        suppressInlineErrorSwal: true,
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      if (isAbortError(err)) return;
      setError(err?.response?.data?.error || err?.message || "Failed to load offers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTreesLoading(true);
      try {
        const [settingsRes, listRes] = await Promise.all([
          http.get("/api/v1/settings", { suppressInlineErrorSwal: true }),
          http.post("/api/v1/category/list", {}, { suppressInlineErrorSwal: true }).catch(() => null),
        ]);
        const settingsRows = settingsRes.data?.data || [];
        const byKey = {};
        settingsRows.forEach((r) => {
          if (r?.key) byKey[r.key] = r.value != null ? String(r.value).trim() : "";
        });
        const flat = Array.isArray(listRes?.data?.data) ? listRes.data.data : [];
        const uid10 = flat.find((c) => Number(c.uid) === 10);

        const countriesRoot = byKey.countries_root_category_id || (uid10?._id ? String(uid10._id) : "");

        const [specs, countries] = await Promise.all([
          fetchTcSpecialtyNodes(),
          countriesRoot ? fetchTreeNodes(countriesRoot) : Promise.resolve([]),
        ]);
        if (!cancelled) {
          setSpecFlat(flattenNodes(specs));
          setCountryFlat(flattenNodes(countries));
        }
      } catch {
        if (!cancelled) {
          setSpecFlat([]);
          setCountryFlat([]);
        }
      } finally {
        if (!cancelled) setTreesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!duplicateId || loading) return;
    const src = rows.find((r) => String(r._id) === duplicateId);
    if (!src) return;
    setEditingId(null);
    setForm(formFromOffer(src));
    setSaveError("");
    setModalOpen(true);
    history.replace({ pathname: location.pathname, search: "" });
  }, [duplicateId, loading, rows, history, location.pathname]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setSaveError("");
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(String(row._id));
    setForm(formFromOffer(row));
    setSaveError("");
    setModalOpen(true);
  };

  const duplicate = (row) => {
    history.push(`?duplicate=${encodeURIComponent(String(row._id))}`);
  };

  const specsSummary = form.allSpecialties
    ? "All specialties"
    : form.specialtyIds
        .map((id) => {
          const n = specFlat.find((s) => s.id === id);
          return n?.label || id;
        })
        .filter(Boolean)
        .join(", ") || "None selected";

  const countriesSummary = form.allCountries
    ? "All countries"
    : form.countryCodes
        .map((c) => countryLabelByCode[c] || c)
        .join(", ") || "None selected";

  const payloadFromForm = () => ({
    name: form.name.trim(),
    description: form.description.trim(),
    allSpecialties: form.allSpecialties,
    specialties: form.allSpecialties ? [] : form.specialtyIds,
    allCountries: form.allCountries,
    countries: form.allCountries ? [] : form.countryCodes,
    defaultPrice: {
      amount: Number(form.defaultAmount),
      currency: form.defaultCurrency,
    },
    defaultCompareAt:
      form.defaultCompareAtAmount !== "" && Number(form.defaultCompareAtAmount) > 0
        ? {
            amount: Number(form.defaultCompareAtAmount),
            currency: form.defaultCurrency,
          }
        : null,
    prices: form.prices
      .filter((p) => p.countryCode && p.amount !== "")
      .map((p) => ({
        countryCode: p.countryCode,
        amount: Number(p.amount),
        currency: p.currency,
        compareAtAmount:
          p.compareAtAmount !== "" && Number(p.compareAtAmount) > 0
            ? Number(p.compareAtAmount)
            : null,
      })),
    features: form.features
      .map((f) => ({ label: f.label.trim(), value: f.value.trim() }))
      .filter((f) => f.label || f.value),
    badgeLabel: form.badgeLabel.trim(),
    highlighted: form.highlighted,
    includesVisio: form.includesVisio,
    slaDays: Number(form.slaDays) || 7,
    active: form.active,
  });

  const save = async () => {
    setSaveBusy(true);
    setSaveError("");
    try {
      const body = payloadFromForm();
      if (editingId) {
        await http.put(`/api/v1/admin/second-opinion/offers/${editingId}`, body);
      } else {
        await http.post("/api/v1/admin/second-opinion/offers", body);
      }
      setModalOpen(false);
      await load(undefined);
    } catch (err) {
      setSaveError(err?.response?.data?.error || err?.message || "Could not save");
    } finally {
      setSaveBusy(false);
    }
  };

  const remove = async (row) => {
    const ok = window.confirm(`Delete offer "${row.name || row._id}"? This cannot be undone.`);
    if (!ok) return;
    setDeleteBusy(String(row._id));
    setError("");
    try {
      await http.delete(`/api/v1/admin/second-opinion/offers/${row._id}`);
      await load(undefined);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not delete");
    } finally {
      setDeleteBusy(null);
    }
  };

  return (
    <>
      <Head title="Second opinion offers" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page tag="h3">
                Second opinion offers
              </BlockTitle>
              <p className="text-soft">
                Formulas shown to patients. Specialties and countries come from Platform catalog roots.
              </p>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button color="primary" onClick={openCreate}>
                <Icon name="plus" />
                <span>New offer</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          {error ? (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          ) : null}

          {loading ? (
            <div className="py-5 text-center">
              <Spinner size="sm" /> Loading…
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Specialties</th>
                    <th>Countries</th>
                    <th>Default price</th>
                    <th>Video</th>
                    <th>Active</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-muted text-center py-4">
                        No offers yet. Create one to enable patient checkout.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row._id}>
                        <td>
                          <div className="fw-bold">{row.name || "—"}</div>
                          {row.description ? (
                            <div className="small text-muted text-truncate" style={{ maxWidth: 240 }}>
                              {row.description}
                            </div>
                          ) : null}
                        </td>
                        <td className="small">{coverageSpecs(row)}</td>
                        <td className="small">{coverageCountries(row)}</td>
                        <td>{displayPrice(row)}</td>
                        <td>{row.includesVisio ? "Yes" : "No"}</td>
                        <td>
                          {row.active !== false ? (
                            <span className="badge badge-dim badge-success">Active</span>
                          ) : (
                            <span className="badge badge-dim badge-secondary">Inactive</span>
                          )}
                        </td>
                        <td className="text-end text-nowrap">
                          <Button size="sm" color="light" className="me-1" onClick={() => openEdit(row)}>
                            Edit
                          </Button>
                          <Button size="sm" color="light" className="me-1" onClick={() => duplicate(row)}>
                            Duplicate
                          </Button>
                          <Button
                            size="sm"
                            color="danger"
                            outline
                            disabled={deleteBusy === String(row._id)}
                            onClick={() => remove(row)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Block>

        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
          <ModalHeader toggle={() => setModalOpen(false)}>
            {editingId ? "Edit offer" : "New offer"}
          </ModalHeader>
          <ModalBody>
            {saveError ? <Alert color="danger">{saveError}</Alert> : null}

            <FormGroup>
              <Label>Offer name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Express review 48h"
              />
            </FormGroup>
            <FormGroup>
              <Label>Description</Label>
              <Input
                type="textarea"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short summary for the patient"
              />
            </FormGroup>
            <FormGroup>
              <Label>Badge label (optional)</Label>
              <Input
                value={form.badgeLabel}
                onChange={(e) => setForm({ ...form, badgeLabel: e.target.value })}
                placeholder='e.g. Most chosen'
                maxLength={80}
              />
              <small className="text-muted">Shown as a pill on the patient formula card.</small>
            </FormGroup>
            <FormGroup check className="mb-3">
              <CustomInput
                type="checkbox"
                id="so-highlighted"
                checked={form.highlighted}
                onChange={(e) => setForm({ ...form, highlighted: e.target.checked })}
                label="Highlight this offer (recommended — only one at a time)"
              />
            </FormGroup>

            <div className="row mb-3">
              <div className="col-md-6">
                <div className="d-flex justify-content-between align-items-start border rounded p-3 h-100">
                  <div>
                    <div className="small text-muted">Specialties</div>
                    <div className="fw-medium">{specsSummary}</div>
                  </div>
                  <Button color="link" className="p-0" onClick={() => setPicker("specs")}>
                    Edit
                  </Button>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex justify-content-between align-items-start border rounded p-3 h-100">
                  <div>
                    <div className="small text-muted">Countries</div>
                    <div className="fw-medium">{countriesSummary}</div>
                  </div>
                  <Button color="link" className="p-0" onClick={() => setPicker("countries")}>
                    Edit
                  </Button>
                </div>
              </div>
            </div>

            <h6 className="mb-1">Price</h6>
            <p className="small text-muted">
              One default price, plus optional country-specific amounts. The patient sees their country
              price, otherwise the default.
            </p>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <span className="small text-muted me-1">Default</span>
              <Input
                type="number"
                min={0}
                step="0.01"
                style={{ maxWidth: 120 }}
                value={form.defaultAmount}
                onChange={(e) => setForm({ ...form, defaultAmount: e.target.value })}
                placeholder="Price"
              />
              <Input
                type="number"
                min={0}
                step="0.01"
                style={{ maxWidth: 120 }}
                value={form.defaultCompareAtAmount}
                onChange={(e) => setForm({ ...form, defaultCompareAtAmount: e.target.value })}
                placeholder="Was (optional)"
              />
              <Input
                type="select"
                style={{ maxWidth: 100 }}
                value={form.defaultCurrency}
                onChange={(e) => setForm({ ...form, defaultCurrency: e.target.value })}
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c.toUpperCase()}
                  </option>
                ))}
              </Input>
            </div>
            <p className="small text-muted mb-3">
              Optional “Was” amount is shown struck through next to the live price.
            </p>

            {form.prices.map((row, idx) => (
              <div key={idx} className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <Input
                  type="select"
                  style={{ maxWidth: 160 }}
                  value={row.countryCode}
                  onChange={(e) => {
                    const code = e.target.value;
                    const next = [...form.prices];
                    next[idx] = {
                      ...next[idx],
                      countryCode: code,
                      currency: currencyForCountry(code),
                    };
                    setForm({ ...form, prices: next });
                  }}
                >
                  <option value="">Country…</option>
                  {countryOptions.map((n) => (
                    <option key={n.countryCode} value={n.countryCode}>
                      {n.label} ({n.countryCode})
                    </option>
                  ))}
                </Input>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  style={{ maxWidth: 110 }}
                  value={row.amount}
                  onChange={(e) => {
                    const next = [...form.prices];
                    next[idx] = { ...next[idx], amount: e.target.value };
                    setForm({ ...form, prices: next });
                  }}
                  placeholder="Price"
                />
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  style={{ maxWidth: 110 }}
                  value={row.compareAtAmount || ""}
                  onChange={(e) => {
                    const next = [...form.prices];
                    next[idx] = { ...next[idx], compareAtAmount: e.target.value };
                    setForm({ ...form, prices: next });
                  }}
                  placeholder="Was"
                />
                <Input
                  type="select"
                  style={{ maxWidth: 100 }}
                  value={row.currency}
                  onChange={(e) => {
                    const next = [...form.prices];
                    next[idx] = { ...next[idx], currency: e.target.value };
                    setForm({ ...form, prices: next });
                  }}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c.toUpperCase()}
                    </option>
                  ))}
                </Input>
                <Button
                  color="danger"
                  size="sm"
                  outline
                  onClick={() => setForm({ ...form, prices: form.prices.filter((_, i) => i !== idx) })}
                >
                  <Icon name="trash" />
                </Button>
              </div>
            ))}
            <Button
              color="light"
              size="sm"
              className="mb-4"
              onClick={() =>
                setForm({
                  ...form,
                  prices: [
                    ...form.prices,
                    { countryCode: "", amount: "", compareAtAmount: "", currency: "eur" },
                  ],
                })
              }
            >
              <Icon name="plus" />
              <span>Add country price</span>
            </Button>

            <h6 className="mb-1">Features</h6>
            <p className="small text-muted">
              Benefit lines on the patient card (label alone is enough). Video has its own setting
              below because it triggers a call.
            </p>
            {form.features.map((f, idx) => (
              <div key={idx} className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <Input
                  placeholder="Label"
                  style={{ maxWidth: 200 }}
                  value={f.label}
                  onChange={(e) => {
                    const next = [...form.features];
                    next[idx] = { ...next[idx], label: e.target.value };
                    setForm({ ...form, features: next });
                  }}
                />
                <span className="text-muted">:</span>
                <Input
                  placeholder="Value"
                  style={{ maxWidth: 200 }}
                  value={f.value}
                  onChange={(e) => {
                    const next = [...form.features];
                    next[idx] = { ...next[idx], value: e.target.value };
                    setForm({ ...form, features: next });
                  }}
                />
                <Button
                  color="danger"
                  size="sm"
                  outline
                  onClick={() =>
                    setForm({
                      ...form,
                      features: form.features.filter((_, i) => i !== idx),
                    })
                  }
                >
                  <Icon name="trash" />
                </Button>
              </div>
            ))}
            <Button
              color="light"
              size="sm"
              className="mb-4"
              onClick={() => setForm({ ...form, features: [...form.features, { label: "", value: "" }] })}
            >
              <Icon name="plus" />
              <span>Add a line</span>
            </Button>

            <FormGroup check className="mb-2">
              <CustomInput
                type="checkbox"
                id="so-visio"
                checked={form.includesVisio}
                onChange={(e) => setForm({ ...form, includesVisio: e.target.checked })}
                label="Includes a video call (triggers a video appointment)"
              />
            </FormGroup>
            <FormGroup>
              <Label>Turnaround (days)</Label>
              <Input
                type="number"
                min={1}
                max={90}
                style={{ maxWidth: 120 }}
                value={form.slaDays}
                onChange={(e) => setForm({ ...form, slaDays: e.target.value })}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter className="justify-content-between">
            <CustomInput
              type="checkbox"
              id="so-active"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              label="Offer active"
            />
            <div>
              <Button color="light" className="me-2" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button color="primary" disabled={saveBusy} onClick={save}>
                {saveBusy ? <Spinner size="sm" /> : "Save"}
              </Button>
            </div>
          </ModalFooter>
        </Modal>

        <Modal isOpen={picker === "specs"} toggle={() => setPicker(null)}>
          <ModalHeader toggle={() => setPicker(null)}>Specialties</ModalHeader>
          <ModalBody>
            <TreePicker
              nodes={specFlat}
              loading={treesLoading}
              selectedIds={form.specialtyIds}
              allFlag={form.allSpecialties}
              allLabel="All specialties"
              onToggleAll={(checked) =>
                setForm({ ...form, allSpecialties: checked, specialtyIds: checked ? [] : form.specialtyIds })
              }
              onToggleId={(id) => {
                const set = new Set(form.specialtyIds);
                if (set.has(id)) set.delete(id);
                else set.add(id);
                setForm({ ...form, specialtyIds: [...set] });
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={() => setPicker(null)}>
              Done
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={picker === "countries"} toggle={() => setPicker(null)}>
          <ModalHeader toggle={() => setPicker(null)}>Countries</ModalHeader>
          <ModalBody>
            <TreePicker
              nodes={countryOptions}
              loading={treesLoading}
              selectedIds={form.countryCodes}
              allFlag={form.allCountries}
              allLabel="All countries"
              idKey="countryCode"
              onToggleAll={(checked) =>
                setForm({ ...form, allCountries: checked, countryCodes: checked ? [] : form.countryCodes })
              }
              onToggleId={(code) => {
                const set = new Set(form.countryCodes);
                if (set.has(code)) set.delete(code);
                else set.add(code);
                setForm({ ...form, countryCodes: [...set] });
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={() => setPicker(null)}>
              Done
            </Button>
          </ModalFooter>
        </Modal>
      </Content>
    </>
  );
};

export default SoOffers;
